import { useState } from "react";
import { Plus, Trash2, Edit2, Check, X, Zap, BookOpen, ChevronDown } from "lucide-react";
import { useShorthands } from "../context/ShorthandContext";
import { SHORTHAND_DICTIONARY, SHORTHAND_CATEGORIES } from "../data/shorthandDictionary";

export default function ShorthandManager() {
  const ctx = useShorthands();
  const entries = ctx?.entries || [];
  const create = ctx?.create || (async () => {});
  const update = ctx?.update || (async () => {});
  const remove = ctx?.remove || (async () => {});
  const [adding, setAdding] = useState(false);
  const [newTrigger, setNewTrigger] = useState("");
  const [newExpansion, setNewExpansion] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [editTrigger, setEditTrigger] = useState("");
  const [editExpansion, setEditExpansion] = useState("");
  const [saving, setSaving] = useState(false);
  const [tab, setTab] = useState("mine"); // mine | dictionary
  const [dictCat, setDictCat] = useState("All");

  const handleAdd = async () => {
    if (!newTrigger.trim() || !newExpansion.trim() || saving) return;
    setSaving(true);
    try {
      await create(newTrigger.trim(), newExpansion.trim());
      setNewTrigger(""); setNewExpansion(""); setAdding(false);
    } finally { setSaving(false); }
  };

  const handleEdit = async (id) => {
    if (!editTrigger.trim() || !editExpansion.trim() || saving) return;
    setSaving(true);
    try {
      await update(id, { trigger: editTrigger.trim(), expansion: editExpansion.trim() });
      setEditingId(null);
    } finally { setSaving(false); }
  };

  const importPreset = async (preset) => {
    const already = entries.find(e => e.trigger === preset.trigger);
    if (already) return;
    setSaving(true);
    try { await create(preset.trigger, preset.expansion); }
    finally { setSaving(false); }
  };

  const filteredDict = dictCat === "All"
    ? SHORTHAND_DICTIONARY
    : SHORTHAND_DICTIONARY.filter(e => e.category === dictCat);

  return (
    <div className="p-4" style={{ fontFamily: "'DM Sans', sans-serif" }}>
      {/* Header + tabs */}
      <div className="flex items-center justify-between mb-3">
        <div>
          <h3 className="text-[13px] font-semibold text-[#EDEDED]">Shorthands</h3>
          <p className="text-[10px] text-[#52525B] mt-0.5">Type a trigger + Space to expand anywhere</p>
        </div>
        <button onClick={() => setAdding(true)}
          className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-[#5A90F5] text-white text-[11px] font-medium hover:bg-[#4a80e5]">
          <Plus className="w-3 h-3"/> Add
        </button>
      </div>

      {/* Tab bar */}
      <div className="flex gap-0.5 bg-[#0D0D0D] border border-[#1E1E1E] rounded-lg p-0.5 mb-3">
        {[["mine", "My Shorthands"], ["dictionary", "Dictionary"]].map(([id, label]) => (
          <button key={id} onClick={() => setTab(id)}
            className={`flex-1 py-1 rounded-md text-[11px] font-medium transition-colors ${tab === id ? "bg-[#1a1a1a] text-[#EDEDED]" : "text-[#52525B] hover:text-[#A1A1AA]"}`}>
            {label}
          </button>
        ))}
      </div>

      {tab === "mine" && (
        <>
          {/* Add row */}
          {adding && (
            <div className="flex items-center gap-2 mb-3 p-2.5 bg-[#0D0D0D] rounded-lg border border-[#1E1E1E]">
              <Zap className="w-3.5 h-3.5 text-[#5A90F5] flex-shrink-0"/>
              <input autoFocus value={newTrigger} onChange={e => setNewTrigger(e.target.value)}
                placeholder="!trigger"
                className="w-24 bg-transparent text-[11px] text-[#EDEDED] outline-none placeholder-[#3A3A3A] border-b border-[#2A2A2A] pb-0.5"
                onKeyDown={e => e.key === "Enter" && handleAdd()}
              />
              <span className="text-[#3A3A3A] text-[11px]">→</span>
              <input value={newExpansion} onChange={e => setNewExpansion(e.target.value)}
                placeholder="Expansion text"
                className="flex-1 bg-transparent text-[11px] text-[#EDEDED] outline-none placeholder-[#3A3A3A] border-b border-[#2A2A2A] pb-0.5"
                onKeyDown={e => e.key === "Enter" && handleAdd()}
              />
              <button onClick={handleAdd} disabled={saving} className="text-[#5A90F5]"><Check className="w-4 h-4"/></button>
              <button onClick={() => { setAdding(false); setNewTrigger(""); setNewExpansion(""); }} className="text-[#52525B]"><X className="w-4 h-4"/></button>
            </div>
          )}

          {entries.length === 0 && !adding ? (
            <div className="text-center py-8 text-[#252525] text-[11px]">
              No shorthands yet.<br/>
              <button onClick={() => setTab("dictionary")} className="text-[#5A90F5] mt-1 hover:underline">Browse the dictionary</button>
            </div>
          ) : (
            <div className="space-y-0.5">
              {entries.map(entry => (
                <div key={entry.entry_id}
                  className="group flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-[#0D0D0D] transition-colors">
                  {editingId === entry.entry_id ? (
                    <>
                      <Zap className="w-3 h-3 text-[#5A90F5] flex-shrink-0"/>
                      <input autoFocus value={editTrigger} onChange={e => setEditTrigger(e.target.value)}
                        className="w-24 bg-[#141414] border border-[#2A2A2A] rounded px-1.5 py-0.5 text-[11px] text-[#EDEDED] outline-none"
                        onKeyDown={e => e.key === "Enter" && handleEdit(entry.entry_id)}
                      />
                      <span className="text-[#3A3A3A] text-[10px]">→</span>
                      <input value={editExpansion} onChange={e => setEditExpansion(e.target.value)}
                        className="flex-1 bg-[#141414] border border-[#2A2A2A] rounded px-1.5 py-0.5 text-[11px] text-[#EDEDED] outline-none"
                        onKeyDown={e => e.key === "Enter" && handleEdit(entry.entry_id)}
                      />
                      <button onClick={() => handleEdit(entry.entry_id)} className="text-[#5A90F5]"><Check className="w-3.5 h-3.5"/></button>
                      <button onClick={() => setEditingId(null)} className="text-[#52525B]"><X className="w-3.5 h-3.5"/></button>
                    </>
                  ) : (
                    <>
                      <Zap className="w-3 h-3 text-[#5A90F5] flex-shrink-0"/>
                      <code className="text-[11px] font-mono text-[#EDEDED] bg-[#141414] px-1.5 py-0.5 rounded">{entry.trigger}</code>
                      <span className="text-[#3A3A3A] text-[10px]">→</span>
                      <span className="flex-1 text-[11px] text-[#A1A1AA] truncate">{entry.expansion}</span>
                      <button onClick={() => { setEditingId(entry.entry_id); setEditTrigger(entry.trigger); setEditExpansion(entry.expansion); }}
                        className="opacity-0 group-hover:opacity-100 text-[#52525B] hover:text-[#EDEDED] p-1 transition-all">
                        <Edit2 className="w-3 h-3"/>
                      </button>
                      <button onClick={() => remove(entry.entry_id)}
                        className="opacity-0 group-hover:opacity-100 text-[#52525B] hover:text-[#EF4444] p-1 transition-all">
                        <Trash2 className="w-3 h-3"/>
                      </button>
                    </>
                  )}
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {tab === "dictionary" && (
        <>
          {/* Category filter */}
          <div className="flex gap-1 flex-wrap mb-3">
            {["All", ...SHORTHAND_CATEGORIES].map(cat => (
              <button key={cat} onClick={() => setDictCat(cat)}
                className={`px-2 py-0.5 rounded-full text-[10px] font-medium transition-colors ${dictCat === cat ? "bg-[#5A90F5] text-white" : "bg-[#0D0D0D] text-[#52525B] hover:text-[#A1A1AA] border border-[#1E1E1E]"}`}>
                {cat}
              </button>
            ))}
          </div>

          <div className="space-y-0.5">
            {filteredDict.map(preset => {
              const imported = entries.some(e => e.trigger === preset.trigger);
              return (
                <div key={preset.trigger}
                  className="group flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-[#0D0D0D] transition-colors">
                  <Zap className="w-3 h-3 text-[#3A3A3A] flex-shrink-0"/>
                  <code className="text-[11px] font-mono text-[#EDEDED] bg-[#141414] px-1.5 py-0.5 rounded w-20 text-center">{preset.trigger}</code>
                  <span className="text-[#3A3A3A] text-[10px]">→</span>
                  <span className="flex-1 text-[11px] text-[#A1A1AA] truncate">{preset.expansion}</span>
                  <span className="text-[9px] text-[#2A2A2A] px-1.5">{preset.category}</span>
                  {imported ? (
                    <span className="text-[10px] text-[#10B981] flex items-center gap-0.5 opacity-0 group-hover:opacity-100">
                      <Check className="w-3 h-3"/> Added
                    </span>
                  ) : (
                    <button onClick={() => importPreset(preset)} disabled={saving}
                      className="text-[10px] text-[#5A90F5] hover:text-white opacity-0 group-hover:opacity-100 transition-all px-2 py-0.5 rounded border border-[#5A90F5]/30 hover:bg-[#5A90F5]/20">
                      + Add
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}
